import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import * as api from '../services/api';
import { isAdminPortalRole, type AuthUser } from '../types/auth';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isSchoolAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = api.getAccessToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    api
      .getMyProfile()
      .then((profile) => {
        if (!isAdminPortalRole(profile.role)) {
          api.clearTokens();
          setUser(null);
          return;
        }
        setUser(profile);
      })
      .catch(() => {
        api.clearTokens();
        setUser(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const result = await api.login(username, password);
    if (!isAdminPortalRole(result.user.role)) {
      throw new Error('Only Super Admins and School Admins can access the TFM Admin portal.');
    }
    if (result.user.status !== 'active') {
      throw new Error('This account is not active yet. Contact a platform administrator.');
    }
    api.setTokens(result.accessToken, result.refreshToken);
    const profile = await api.getMyProfile();
    if (!isAdminPortalRole(profile.role)) {
      api.clearTokens();
      throw new Error('Only Super Admins and School Admins can access the TFM Admin portal.');
    }
    setUser(profile);
  }, []);

  const logout = useCallback(() => {
    api.clearTokens();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user),
      isSuperAdmin: user?.role === 'SUPER_ADMIN',
      isSchoolAdmin: user?.role === 'SCHOOL_ADMIN',
      login,
      logout
    }),
    [user, isLoading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
